/**
 * RightRail — Shura Nexus side rail (Palantir-deep edition).
 *
 * Three intelligence-brief cards:
 *   1. AI Discoveries — hidden-link recommendations from Iqra
 *   2. Connection-type legend + edge-strength reference
 *   3. Time-travel slider (2024 → 2026)
 *
 * Each card uses the shared `.pal-card .pal-card-live .pal-grid-bg`
 * utility set so the visual language matches NodePanel + AdminSecurity.
 */
import React from 'react';
import { CONNECTION_LEGEND } from './constants';

export function RightRail({
  isDark, discoveries, narratives, loadingNarrative, onAskIqra,
  stats, timeYear, onTimeYear,
}) {
  return (
    <aside className="col-span-12 lg:col-span-3 space-y-3">
      <DiscoveriesCard
        isDark={isDark}
        discoveries={discoveries}
        narratives={narratives}
        loadingNarrative={loadingNarrative}
        onAskIqra={onAskIqra}
      />
      <LegendCard isDark={isDark} stats={stats} />
      <TimeTravelCard isDark={isDark} timeYear={timeYear} onTimeYear={onTimeYear} />
    </aside>
  );
}

function DiscoveriesCard({ isDark, discoveries, narratives, loadingNarrative, onAskIqra }) {
  return (
    <div className="pal-card pal-card-live pal-grid-bg p-4" data-testid="shura-discoveries">
      <div className="flex items-center justify-between mb-3">
        <span className="pal-bracket">[AI DISCOVERIES]</span>
        <span className="pal-mono text-[10px] opacity-60">N={discoveries.length}</span>
      </div>
      {discoveries.length === 0 ? (
        <div className="text-xs opacity-60 italic">No hidden relationships found yet — graph too sparse.</div>
      ) : (
        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
          {discoveries.slice(0, 5).map((d, i) => {
            const key = [d.a.id, d.b.id].sort().join('|');
            const narrative = narratives[key];
            const isLoading = loadingNarrative === key;
            return (
              <div
                key={i}
                className={`p-2.5 rounded-md text-[11px] border ${
                  isDark
                    ? 'bg-[rgba(174,82,234,0.06)] border-[rgba(174,82,234,0.30)] text-slate-200'
                    : 'bg-violet-50 border-violet-200 text-slate-700'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="pal-bracket text-[10px]">
                    [LINK {String(i + 1).padStart(2, '0')}] · {d.score}
                  </span>
                  {!narrative && !isLoading && (
                    <button
                      onClick={() => onAskIqra(d)}
                      data-testid={`shura-narrative-btn-${i}`}
                      className="pal-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ve-violet-bright)] hover:text-[var(--ve-violet-glow)] transition-colors"
                    >
                      ASK IQRA →
                    </button>
                  )}
                  {isLoading && <span className="pal-mono text-[10px] italic opacity-70">PROCESSING…</span>}
                </div>
                <div className="pal-mono text-[11px]">
                  {d.a.label} <span className="opacity-40">↔</span> {d.b.label}
                </div>
                <div className="text-[10px] mt-1 opacity-60">
                  Via {d.shared_neighbours.join(' · ')}
                </div>
                {narrative && (
                  <div className={`mt-2 pt-2 border-t border-dashed text-[11px] italic leading-relaxed ${
                    isDark ? 'border-[rgba(174,82,234,0.25)] text-violet-100' : 'border-violet-200 text-violet-900'
                  }`}>
                    “{narrative}”
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LegendCard({ isDark, stats }) {
  return (
    <div className="pal-card pal-grid-bg p-4" data-testid="shura-legend">
      <div className="pal-bracket mb-3">[CONNECTION TYPES]</div>
      <div className="space-y-1">
        {CONNECTION_LEGEND.map((c) => {
          const swatch = (!isDark && c.color === '#FFFFFF') ? '#0A0A0A' : c.color;
          return (
            <div key={c.kind} className="pal-row">
              <span className="flex items-center gap-2">
                <span className="h-0.5 w-6 rounded" style={{ backgroundColor: swatch, boxShadow: `0 0 6px ${swatch}88` }} />
                <span className="pal-mono text-[11px]">{c.label}</span>
              </span>
              <span className="pal-val text-[11px] tabular-nums opacity-80">
                {String(stats.edges_by_kind?.[c.kind] || 0).padStart(3, '0')}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-dashed border-[rgba(174,82,234,0.18)]">
        <div className="pal-bracket text-[9px] mb-2">[STRENGTH]</div>
        <div className="flex items-center gap-2 pal-mono text-[10px]">
          <span className={`h-px w-4 ${isDark ? 'bg-white/40' : 'bg-slate-400'}`} />
          <span className="opacity-70">THIN</span>
          <span className={`h-0.5 w-4 ${isDark ? 'bg-white/60' : 'bg-slate-500'}`} />
          <span className="opacity-70">MID</span>
          <span className={`h-1 w-4 ${isDark ? 'bg-white/90' : 'bg-slate-800'}`} />
          <span className="opacity-70">HIGH</span>
        </div>
      </div>
    </div>
  );
}

function TimeTravelCard({ isDark, timeYear, onTimeYear }) {
  return (
    <div className="pal-card pal-grid-bg p-4" data-testid="shura-time-travel">
      <div className="flex items-center justify-between mb-2">
        <span className="pal-bracket">[TIME · TRAVEL]</span>
        <span className="pal-status pal-status-ok">
          <span className="h-1 w-1 rounded-full bg-emerald-500 animate-pulse" /> LIVE
        </span>
      </div>
      <input
        type="range"
        min={2024}
        max={2026}
        step={1}
        value={timeYear}
        onChange={(e) => onTimeYear(Number(e.target.value))}
        className={`w-full mt-2 ${isDark ? 'accent-[#AE52EA]' : 'accent-[#5E1F8B]'}`}
        data-testid="shura-time-travel-slider"
      />
      <div className="flex items-center justify-between text-[10px] mt-1 pal-mono opacity-70">
        <span>2024</span>
        <span className="pal-val text-base">{timeYear}</span>
        <span>2026</span>
      </div>
      <div className="text-[10px] mt-2 leading-relaxed opacity-55 pal-mono">
        edges &gt; {timeYear} fade to 5% — drag to watch the ecosystem assemble.
      </div>
    </div>
  );
}
